import { useCallback, useState } from 'react';

import { isApiError } from '@platform/api-client';
import { Alert, Button, Dialog, Field, Input } from '@platform/ui';

import { useIntegrationsClient } from '../../lib/api/integrations-client-context';

/**
 * The second factor a step-up route asks for (API.md §1.3, SECURITY.md §4.2).
 *
 * Three of the routes on this page — saving credentials, removing them and turning an
 * integration off — answer `403 MFA_STEP_UP_REQUIRED` unless a second factor was verified
 * in the last ten minutes. The contract is: verify at `/auth/two-factor/verify-totp`, then
 * **retry the original call**. Retrying is the part a generic error toast gets wrong, so
 * the retry lives here, next to the prompt.
 *
 * **TODO(M2.7):** replace with the shared `StepUpDialog` from `@platform/ui` (M2.8 is
 * building it, together with passkey verification and backup codes). This one is
 * deliberately minimal — a TOTP code and nothing else — so the integrations screens are
 * not blocked on it and so the swap is one import and one prop.
 *
 * Nothing about the code is stored: it lives in this component's state for as long as the
 * dialog is open and dies with it.
 */

export interface StepUpRequest {
  /** What the admin was doing, named in the dialog so the prompt is not a non sequitur. */
  action: string;
  /** Re-run of the refused call. Rejections come back to the caller's own error handling. */
  retry: () => Promise<void>;
}

export interface UseStepUp {
  /** The pending request, or null. Pass to `<StepUpPrompt>`. */
  pending: StepUpRequest | null;
  /**
   * Runs `work`; if the API asks for a second factor, opens the prompt and re-runs `work`
   * once the code is accepted. Every other failure is rethrown untouched.
   */
  run: (action: string, work: () => Promise<void>) => Promise<void>;
  cancel: () => void;
}

export function useStepUp(): UseStepUp {
  const [pending, setPending] = useState<StepUpRequest | null>(null);

  /* `setPending` is stable across renders, so `run` is created once and never restarts a
     caller's effect or memo. `StepUpRequest` is an object, hence the updater form: a bare
     `setPending(request)` would be read as a state updater function. */
  const run = useCallback(async (action: string, work: () => Promise<void>): Promise<void> => {
    try {
      await work();
    } catch (error) {
      if (!isApiError(error) || !error.isStepUpRequired) throw error;
      setPending(() => ({
        action,
        retry: async () => {
          await work();
        },
      }));
    }
  }, []);

  const cancel = useCallback(() => {
    setPending(null);
  }, []);

  return { pending, run, cancel };
}

export interface StepUpPromptProps {
  request: StepUpRequest | null;
  onClose: () => void;
  /** Called after the retry succeeded, so the screen can toast and refetch. */
  onVerified?: () => void;
}

export function StepUpPrompt({ request, onClose, onVerified }: StepUpPromptProps) {
  const client = useIntegrationsClient();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const close = (): void => {
    setCode('');
    setError(undefined);
    setBusy(false);
    onClose();
  };

  const submit = async (): Promise<void> => {
    if (request === null) return;
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the six digits from your authenticator app.');
      return;
    }

    setBusy(true);
    setError(undefined);
    try {
      await client.verifyTotp(code.trim());
      /* Verified. The original call is re-run here, not by the caller, so the admin
         presses one button rather than two. */
      await request.retry();
      close();
      onVerified?.();
    } catch (cause) {
      setError(
        isApiError(cause) ? cause.message : 'That did not work. Please try again in a moment.',
      );
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={request !== null}
      onClose={close}
      dismissOnBackdrop={false}
      title="Confirm it is you"
      description={
        request === null
          ? undefined
          : `${request.action} needs a second factor. Enter the current code from your authenticator app.`
      }
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void submit();
            }}
            disabled={busy}
          >
            {busy ? 'Checking…' : 'Confirm'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error === undefined ? null : (
          <Alert tone="error" className="mb-4">
            {error}
          </Alert>
        )}
        <Field label="Six-digit code" required={true}>
          <Input
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            disabled={busy}
            onChange={(event) => {
              setCode(event.currentTarget.value.replace(/\D/g, ''));
            }}
          />
        </Field>
      </form>
    </Dialog>
  );
}
